import Chart from 'chart.js/auto';
export { CURRENT_SAMPLE_MS, sampleCurrentDirections } from '../../../utils/currentDirections';

// Plugins y utilidades de dibujo del gráfico de tasa (Detalle diario).
// Se extraen aquí para no engrosar TasaChart.jsx; este módulo solo depende de
// Chart.js y no importa nada de TasaChart (evita dependencias circulares).

// Colores propios del dibujo por canvas
export const yellow = 'rgba(255, 206, 86, 0.5)';
// Corriente (correntómetro): violeta, distinto del resto de métricas del gráfico.
export const currentColor      = 'rgba(179, 136, 255, 0.6)';
export const currentArrowColor = 'rgba(179, 136, 255, 0.95)';

// Flechas de dirección de corriente (correntómetro) — solo en vista expandida.
export const CURRENT_ARROW_BAND = 46;             // px reservados bajo el eje X para las flechas y la brújula
export const CURRENT_ARROW_LEN  = 14;             // largo de la flecha en px

// Columna de resaltado en hover: ancho dependiente de la pantalla, centrada en el punto más cercano.
export const HOVER_COLUMN_WIDTH = window.innerWidth * 0.01;
export const HOVER_COLUMN_COLOR = 'rgba(255, 255, 255, 0.06)';

// Dibuja una columna sutil de ancho fijo centrada en el punto sobre el que se
// hace hover, para dar noción de qué sección se está mirando (sin replicar la
// grilla). Se apoya en los elementos activos que Chart.js calcula al pasar el
// cursor; al salir, no hay activos y la columna desaparece en el siguiente draw.
export const hoverColumnPlugin = {
  id: 'hoverColumn',
  beforeDatasetsDraw(chart) {
    const active = chart.getActiveElements?.();
    if (!active || active.length === 0) return;

    const { chartArea, ctx } = chart;
    if (!chartArea || !ctx) return;

    const x = active[0]?.element?.x;
    if (!Number.isFinite(x)) return;

    const opts = chart.options?.plugins?.hoverColumn;
    const width = opts?.width ?? HOVER_COLUMN_WIDTH;
    const color = opts?.color ?? HOVER_COLUMN_COLOR;
    const left = Math.max(chartArea.left, x - width / 2);
    const right = Math.min(chartArea.right, x + width / 2);

    ctx.save();
    ctx.fillStyle = color;
    ctx.fillRect(left, chartArea.top, right - left, chartArea.bottom - chartArea.top);
    ctx.restore();
  },
};

// Dibuja, tras la campana de alarma, la línea semitransparente hasta el fin de reacción.
export const alarmReactionPlugin = {
  id: 'alarmReaction',
  beforeDatasetDraw(chart, { index }) {
    const dataset = chart.data.datasets[index];
    if (!dataset?.isAlarm) return;

    const points = chart.getDatasetMeta(index)?.data;
    const xScale = chart.scales?.x;
    const { chartArea, ctx } = chart;
    if (!Array.isArray(points) || !xScale || !chartArea || !ctx) return;

    ctx.save();
    ctx.beginPath();
    ctx.rect(chartArea.left, chartArea.top, chartArea.right - chartArea.left, chartArea.bottom - chartArea.top);
    ctx.clip();
    points.forEach((point, pointIndex) => {
      const raw = dataset.data[pointIndex];
      if (!Number.isFinite(raw?.reactionEndX) || !Number.isFinite(point?.x) || !Number.isFinite(point?.y)) return;
      const endX = xScale.getPixelForValue(raw.reactionEndX);
      if (!Number.isFinite(endX)) return;
      // La campana ocupa unos 14 px: ocultamos bajo ella el inicio de la línea semitransparente.
      const lineStartX = Math.min(point.x + 7, endX);

      ctx.beginPath();
      ctx.strokeStyle = raw.markerColor || yellow;
      ctx.lineWidth = 2;
      ctx.moveTo(lineStartX, point.y);
      ctx.lineTo(endX, point.y);
      ctx.moveTo(endX, point.y - 6);
      ctx.lineTo(endX, point.y + 6);
      ctx.stroke();
    });
    ctx.restore();
  },
};

// Dibuja bajo el eje X (sobre la leyenda) una fila de flechas que indican la
// dirección de la corriente. Convención oceanográfica: la flecha apunta hacia
// donde va la corriente (0° = Norte/arriba, sentido horario). Las muestras y el
// color llegan por options.plugins.currentDirection; solo se registra en el modal.
export const currentDirectionPlugin = {
  id: 'currentDirection',
  afterDraw(chart) {
    const opts = chart.options?.plugins?.currentDirection;
    const samples = opts?.samples;
    if (!Array.isArray(samples) || samples.length === 0) return;

    const xScale = chart.scales?.x;
    const { chartArea, ctx } = chart;
    if (!xScale || !chartArea || !ctx) return;

    const color = opts.color || currentArrowColor;
    const band = opts.band ?? CURRENT_ARROW_BAND;
    const len = opts.length ?? CURRENT_ARROW_LEN;
    const half = len / 2;
    const head = Math.max(3, len * 0.32);
    // Centro vertical de las flechas: en la parte baja de la caja del eje X,
    // por debajo de los ticks y por encima de la leyenda.
    const yCenter = xScale.bottom - band / 2;

    // ----- Brújula de referencia (a la izquierda del todo, bajo los ejes) -----
    // Réplica del icono CurrentMeterCompass (IconSensor.jsx): anillo exterior,
    // letras cardinales y aguja apuntando al Norte (arriba). Escalada desde su
    // viewBox 100x100. Es una referencia estática: indica que arriba = Norte.
    // El anillo se dibuja algo más grande y la aguja algo más pequeña, para que
    // se lea con claridad como una brújula.
    const compassR = Math.min(band / 2 - 5, 17);
    const compassX = compassR + 8;
    const ringScale = compassR / 42;         // el círculo del SVG tiene r=42 en un viewBox 100x100
    const needleScale = ringScale * 0.72;    // aguja reducida respecto al anillo
    const ringX = (x) => compassX + (x - 50) * ringScale;
    const ringY = (y) => yCenter + (y - 50) * ringScale;
    const needleX = (x) => compassX + (x - 50) * needleScale;
    const needleY = (y) => yCenter + (y - 50) * needleScale;

    ctx.save();
    // Anillo exterior
    ctx.strokeStyle = color;
    ctx.globalAlpha = 0.45;
    ctx.lineWidth = Math.max(1, 4 * ringScale);
    ctx.beginPath();
    ctx.arc(compassX, yCenter, compassR, 0, Math.PI * 2);
    ctx.stroke();
    // Letras cardinales
    ctx.globalAlpha = 0.72;
    ctx.fillStyle = color;
    ctx.font = `700 ${Math.max(6, 18 * ringScale)}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText('N', ringX(50), ringY(17));
    ctx.fillText('S', ringX(50), ringY(94));
    ctx.fillText('E', ringX(90), ringY(55));
    ctx.fillText('O', ringX(10), ringY(55));
    // Aguja: punta (arrowhead) hacia el Norte + tallo
    ctx.globalAlpha = 1;
    ctx.fillStyle = color;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(needleX(50), needleY(22));
    ctx.lineTo(needleX(60), needleY(54));
    ctx.lineTo(needleX(50), needleY(47));
    ctx.lineTo(needleX(40), needleY(54));
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = color;
    ctx.lineCap = 'round';
    ctx.lineWidth = Math.max(1.5, 6 * needleScale);
    ctx.beginPath();
    ctx.moveTo(needleX(50), needleY(47));
    ctx.lineTo(needleX(50), needleY(78));
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.beginPath();
    ctx.rect(chartArea.left, yCenter - len, chartArea.right - chartArea.left, len * 2);
    ctx.clip();
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 1.5;

    samples.forEach((sample) => {
      if (!Number.isFinite(sample.cd)) return;
      const px = xScale.getPixelForValue(sample.x);
      if (!Number.isFinite(px) || px < chartArea.left || px > chartArea.right) return;

      ctx.save();
      ctx.translate(px, yCenter);
      ctx.rotate((sample.cd * Math.PI) / 180); // 0° = arriba (Norte), horario
      // Cuerpo de la flecha
      ctx.beginPath();
      ctx.moveTo(0, half);
      ctx.lineTo(0, -half);
      ctx.stroke();
      // Punta (apuntando hacia donde va la corriente)
      ctx.beginPath();
      ctx.moveTo(0, -half);
      ctx.lineTo(-head * 0.6, -half + head);
      ctx.lineTo(head * 0.6, -half + head);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    });

    ctx.restore();
  },
};

// Deja la entrada de leyenda de alarmas siempre en amarillo, pase lo que pase.
export const generateTasaLegendLabels = (chart) => Chart.defaults.plugins.legend.labels
  .generateLabels(chart)
  .map((label) => {
    const dataset = chart.data.datasets[label.datasetIndex];
    if (!dataset?.isAlarm) return label;
    return {
      ...label,
      fillStyle: yellow,
      strokeStyle: yellow,
      lineWidth: 0,
    };
  });
